import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, Package, Layers, Coins,
  PlayCircle, FileText, ShieldCheck, Sparkles, Database, LogOut, Terminal, FileBarChart, DollarSign, PieChart,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import DemoModeToggle from "@/components/layout/DemoModeToggle";

const items = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/monthly-run", label: "Monthly GGR Run", icon: PlayCircle, testid: "nav-monthly-run" },
  { to: "/monthly-overview", label: "Monthly Overview", icon: PieChart, testid: "nav-monthly-overview" },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/payments", label: "Payments", icon: ShieldCheck, testid: "nav-payments" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports" },
  { to: "/tally", label: "Tally", icon: ShieldCheck, testid: "nav-tally" },
  { to: "/insights", label: "AI Insights", icon: Sparkles, testid: "nav-insights" },
];

const settingsItems = [
  { to: "/providers", label: "Providers", icon: Package, testid: "nav-providers" },
  { to: "/aggregators", label: "Aggregators", icon: Layers, testid: "nav-aggregators" },
  { to: "/brands", label: "Brands", icon: Building2, testid: "nav-brands" },
  { to: "/fx-rates", label: "FX Rates", icon: Coins, testid: "nav-fx-rates" },
  { to: "/admin/currencies", label: "Currencies", icon: DollarSign, testid: "nav-admin-currencies" },
];

const adminItems = [
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-admin-users" },
  { to: "/admin/db", label: "DB Connection", icon: Database, testid: "nav-admin-db" },
  { to: "/admin/sql", label: "SQL Editor", icon: Terminal, testid: "nav-admin-sql" },
];

function Item({ to, label, icon: Icon, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
          isActive
            ? "bg-white/80 text-[var(--wv-primary)] font-medium shadow-[0_1px_0_rgba(255,255,255,0.95)_inset,0_6px_14px_-8px_rgba(28,40,60,0.15)]"
            : "text-[var(--wv-text-2)] hover:bg-white/60 hover:text-[var(--wv-text)]"
        }`
      }
    >
      <Icon size={18} strokeWidth={1.5} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const isAdmin = user && user.role === "admin";
  return (
    <aside className="hidden lg:flex flex-col w-64 wv-sidebar min-h-screen sticky top-0">
      <div className="px-6 pt-6 pb-4">
        <div className="wv-overline">Console</div>
        <div className="mt-1 text-xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>
          Wellness Vibes
        </div>
      </div>

      <div className="px-4 pb-4 border-b border-white/50 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm text-[var(--wv-text)] font-medium truncate" data-testid="sidebar-user-email">{user?.email}</div>
            <div className="text-xs text-[var(--wv-text-3)] mt-0.5">
              {user?.role} {user?.is_super_admin ? "· super" : ""}
            </div>
          </div>
          <button
            data-testid="sidebar-logout"
            onClick={logout}
            title="Sign out"
            className="shrink-0 flex items-center gap-1.5 text-xs text-[var(--wv-text-2)] hover:text-[var(--wv-error)] transition-colors px-2 py-1 rounded-md hover:bg-white/60"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
        {isAdmin && <DemoModeToggle />}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((it) => <Item key={it.to} {...it} />)}
        {isAdmin && (
          <>
            <div className="wv-overline mt-6 px-3">Settings</div>
            {settingsItems.map((it) => <Item key={it.to} {...it} />)}
            <div className="wv-overline mt-6 px-3">Admin</div>
            {adminItems.map((it) => <Item key={it.to} {...it} />)}
          </>
        )}
      </nav>
    </aside>
  );
}
